import React, { useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import {
    PhoneOff, MapPin, BookOpen, Smartphone, MessageCircle,
    Coffee, Users, TrendingUp, Heart, Sparkles, Target, Flame,
    Calendar, ArrowRight, Check, X, Eye, Bookmark, Share2,
    AlertCircle, Layers, Wrench, ChevronDown, QrCode, Zap,
    Image as PolaroidIcon,
} from 'lucide-react';

// ───────────────────────────── Link'd brand tokens (lifted from linkd.social) ─────────────────────────────
const LINKD = {
    night: '#0a0717',
    ink: '#1b0f3a',
    inkSoft: '#2c1d5a',
    cream: '#f5eedc',
    paper: '#faf4e6',
    paperSoft: '#f0e5cc',
    lavender: '#bc96ff',
    lavenderBright: '#d6b8ff',
    lavenderSoft: '#e6d6ff',
    lavenderDeep: '#7d55d6',
    orange: '#ff9f5a',
    orangeDeep: '#e76f3b',
    amber: '#f7c873',
    mindaro: '#d7ff81',
    mindaroDeep: '#9ecf3a',
    neonRose: '#ff4f8b',
};

// ───────────────────────────── Helpers ─────────────────────────────
const fadeUp = {
    hidden: { opacity: 0, y: 32 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const Section = ({ children, className = "", id }) => (
    <motion.section
        id={id}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-80px" }}
        variants={fadeUp}
        className={`relative py-24 md:py-32 ${className}`}
    >
        {children}
    </motion.section>
);

const Eyebrow = ({ n, children, color = LINKD.lavender }) => (
    <div className="flex items-center gap-3 mb-8">
        <span className="font-mono text-[11px] tracking-[0.35em]" style={{ color }}>
            {String(n).padStart(2, '0')}
        </span>
        <span className="h-px flex-1" style={{ background: `linear-gradient(to right, ${color}55, transparent)` }}></span>
        <span className="font-mono text-[10px] tracking-[0.4em] uppercase" style={{ color: `${LINKD.cream}77` }}>
            {children}
        </span>
    </div>
);

const Display = ({ children, className = "", italic = false }) => (
    <h2
        className={`font-display tracking-tight leading-[0.95] ${className}`}
        style={{
            fontFamily: '"Fraunces", "Times New Roman", serif',
            fontVariationSettings: italic ? '"SOFT" 80, "WONK" 1' : '"SOFT" 30, "WONK" 0',
            fontStyle: italic ? 'italic' : 'normal',
        }}
    >
        {children}
    </h2>
);

const Pill = ({ children, color = LINKD.lavender }) => (
    <span
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium tracking-wide border backdrop-blur-sm"
        style={{
            color,
            borderColor: `${color}40`,
            background: `${color}10`,
        }}
    >
        {children}
    </span>
);

// ───────────────────────────── Decorative SVG blobs ─────────────────────────────
const Blob = ({ color, className = "", style = {} }) => (
    <div
        className={`absolute rounded-full filter blur-[100px] opacity-30 pointer-events-none ${className}`}
        style={{ background: color, ...style }}
    />
);

// ───────────────────────────── Data ─────────────────────────────
const competitors = [
    { name: "Bumble IN", initial: "B", weak: "talks IRL, isn't IRL" },
    { name: "Hinge", initial: "H", weak: "barely India-localized" },
    { name: "Tinder IN", initial: "T", weak: "no fatigue answer" },
    { name: "Aisle", initial: "A", weak: "small reach" },
    { name: "QuackQuack", initial: "Q", weak: "no IG voice" },
    { name: "TrulyMadly", initial: "T", weak: "quiet on social" },
];

const pillars = [
    { icon: MessageCircle, name: "Still Texting?", sub: "Anti-app comedy", mix: 35, hook: '"Day 19 of we should grab coffee."', color: LINKD.lavender },
    { icon: MapPin, name: "This Week, IRL", sub: "City as character", mix: 20, hook: '"Bandra cafés you can leave in 60 min."', color: LINKD.amber },
    { icon: PolaroidIcon, name: "Real Meets", sub: "Story-format", mix: 20, hook: '"Met for 30 min Tuesday. Saw her Friday."', color: LINKD.orange },
    { icon: BookOpen, name: "Dating Diaries", sub: "Editorial hot takes", mix: 15, hook: '"Situationships = cowardice with extra steps."', color: LINKD.neonRose },
    { icon: Smartphone, name: "How Link'd Works", sub: "Soft product", mix: 10, hook: "1 vlog/wk · never hero-ad energy", color: LINKD.mindaro },
];

const phases = [
    { tag: "P0", weeks: "W1–2", title: "Foundation", goal: "Voice + visual lock", color: LINKD.cream },
    { tag: "P1", weeks: "W3–6", title: "Establish", goal: "5K · 3 reels >50K · save ≥4%", color: LINKD.amber },
    { tag: "P2", weeks: "W7–12", title: "Traction", goal: "25K · 1 reel >1M · save ≥5%", color: LINKD.lavender },
    { tag: "P3", weeks: "M4–6", title: "Community", goal: "IRL flywheel + paid layer", color: LINKD.neonRose },
];

const engine = [
    { icon: Eye, num: "12–16", label: "Reels" },
    { icon: Layers, num: "6–8", label: "Carousels" },
    { icon: Bookmark, num: "4", label: "Statics" },
    { icon: Share2, num: "30+", label: "Stories" },
    { icon: Users, num: "2–4", label: "Collabs" },
];

const kpis = [
    { num: "0→25K", label: "Followers" },
    { num: "≥5%", label: "Save-rate" },
    { num: "1×", label: ">1M reel" },
    { num: "6+", label: "Collabs" },
];

const questions = [
    { icon: Users, q: "Creator budget" },
    { icon: Calendar, q: "IRL events live?" },
    { icon: MapPin, q: "City priority" },
    { icon: Wrench, q: "Brand assets" },
    { icon: AlertCircle, q: "Compliance" },
    { icon: QrCode, q: "App in stores?" },
    { icon: TrendingUp, q: "Paid budget" },
];

// ───────────────────────────── Page ─────────────────────────────
export default function LinkdProposal() {
    const { scrollYProgress } = useScroll();
    const heroOpacity = useTransform(scrollYProgress, [0, 0.08], [1, 0]);
    const heroScale = useTransform(scrollYProgress, [0, 0.1], [1, 0.94]);

    useEffect(() => {
        document.title = "Link'd × SocialSEO — IG Proposal";
        // Inject Link'd-matching fonts (Fraunces + Inter)
        const id = 'linkd-fonts';
        if (!document.getElementById(id)) {
            const link = document.createElement('link');
            link.id = id;
            link.rel = 'stylesheet';
            link.href = 'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght,SOFT,WONK@9..144,300..900,0..100,0..1&family=Inter:wght@300;400;500;600&display=swap';
            document.head.appendChild(link);
        }
    }, []);

    return (
        <div
            className="min-h-screen overflow-x-hidden selection:bg-[#bc96ff]/40 selection:text-[#0a0717]"
            style={{
                background: LINKD.night,
                color: LINKD.cream,
                fontFamily: '"Inter", system-ui, sans-serif',
            }}
        >
            {/* Override the global animated indigo bg with night-aubergine */}
            <div className="fixed inset-0 z-0 pointer-events-none" style={{ background: LINKD.night }} />

            {/* Ambient lavender + orange blobs */}
            <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
                <Blob color={LINKD.lavender} className="w-[600px] h-[600px] -top-40 -right-40" style={{ opacity: 0.18 }} />
                <Blob color={LINKD.orange} className="w-[500px] h-[500px] top-1/3 -left-40" style={{ opacity: 0.10 }} />
                <Blob color={LINKD.lavenderDeep} className="w-[700px] h-[700px] bottom-0 right-1/4" style={{ opacity: 0.12 }} />
            </div>

            {/* Grain */}
            <div
                className="fixed inset-0 z-0 pointer-events-none opacity-[0.06] mix-blend-overlay"
                style={{ backgroundImage: 'url("https://www.transparenttextures.com/patterns/grain.png")' }}
            />

            <div className="relative z-10">
                {/* ─── Header ─── */}
                <header className="fixed top-0 left-0 right-0 z-50 px-6 py-5 flex justify-between items-center pointer-events-none">
                    <div
                        className="px-4 py-2 rounded-full pointer-events-auto backdrop-blur-md border"
                        style={{ background: `${LINKD.night}99`, borderColor: `${LINKD.cream}15` }}
                    >
                        <span className="font-bold tracking-tight text-sm">
                            Social<span style={{ color: LINKD.lavender }}>SEO</span>
                        </span>
                    </div>
                    <div
                        className="px-4 py-2 rounded-full pointer-events-auto backdrop-blur-md border flex items-center gap-2"
                        style={{ background: `${LINKD.night}99`, borderColor: `${LINKD.lavender}30` }}
                    >
                        <Heart className="w-3 h-3" style={{ color: LINKD.lavender }} />
                        <span className="text-[10px] font-mono tracking-[0.3em] uppercase" style={{ color: LINKD.cream }}>
                            Link'd · IG Proposal
                        </span>
                    </div>
                </header>

                {/* ─── 1. HERO ─── */}
                <motion.section
                    style={{ opacity: heroOpacity, scale: heroScale }}
                    className="min-h-screen flex flex-col justify-center items-center text-center px-6 pt-32"
                >
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full border mb-10 backdrop-blur-sm"
                        style={{ borderColor: `${LINKD.lavender}40`, background: `${LINKD.lavender}0d` }}
                    >
                        <PhoneOff className="w-3.5 h-3.5" style={{ color: LINKD.lavender }} />
                        <span className="text-[10px] font-mono tracking-[0.35em] uppercase" style={{ color: LINKD.lavenderBright }}>
                            Stop Doomscrolling · Start Meeting
                        </span>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <Display className="text-[clamp(3rem,9vw,8rem)] mb-2" italic>
                            <span style={{ color: LINKD.cream }}>Anti-doomscroll</span>
                        </Display>
                        <Display className="text-[clamp(3rem,9vw,8rem)]">
                            <span style={{ color: LINKD.lavender }}>dating app.</span>
                        </Display>
                        <Display className="text-[clamp(2rem,5vw,4.5rem)] mt-6" italic>
                            <span style={{ color: `${LINKD.cream}70` }}>Anti-feed Instagram.</span>
                        </Display>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 1 }}
                        className="flex flex-wrap items-center justify-center gap-2.5 mt-14"
                    >
                        <Pill color={LINKD.lavender}><MapPin className="w-3 h-3" />BLR · BOM · DEL</Pill>
                        <Pill color={LINKD.amber}><Layers className="w-3 h-3" />Full IG management</Pill>
                        <Pill color={LINKD.orange}><Calendar className="w-3 h-3" />90-day plan</Pill>
                        <Pill color={LINKD.mindaro}><Sparkles className="w-3 h-3" />~30 pieces / mo</Pill>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.6 }}
                        transition={{ duration: 1, delay: 1.6 }}
                        className="absolute bottom-12 flex flex-col items-center gap-2"
                    >
                        <span className="font-mono text-[10px] tracking-[0.4em] uppercase" style={{ color: `${LINKD.cream}60` }}>
                            scroll
                        </span>
                        <ChevronDown className="w-4 h-4 animate-bounce" style={{ color: LINKD.lavender }} />
                    </motion.div>
                </motion.section>

                {/* ─── 2. REFRAME (sticky-style hook) ─── */}
                <Section className="px-6">
                    <div className="max-w-4xl mx-auto text-center">
                        <Display className="text-[clamp(2rem,5vw,4.5rem)]">
                            <span style={{ color: LINKD.cream }}>You built the dating app</span>
                            <br />
                            <span style={{ color: `${LINKD.cream}55` }}>that doesn&apos;t waste people&apos;s time.</span>
                        </Display>
                        <div className="my-10 mx-auto w-px h-16" style={{ background: `linear-gradient(to bottom, ${LINKD.lavender}, transparent)` }}></div>
                        <Display className="text-[clamp(2rem,5vw,4.5rem)]" italic>
                            <span style={{ color: LINKD.lavender }}>Now you need the Instagram</span>
                            <br />
                            <span style={{ color: LINKD.lavenderBright }}>that doesn&apos;t waste their attention.</span>
                        </Display>
                    </div>
                </Section>

                {/* ─── 3. THE WAVE ─── */}
                <Section className="px-6">
                    <div className="max-w-6xl mx-auto">
                        <Eyebrow n={1}>The Wave</Eyebrow>
                        <Display className="text-[clamp(2rem,4.5vw,3.75rem)] mb-14 max-w-3xl">
                            <span style={{ color: LINKD.cream }}>Dating-app fatigue is the </span>
                            <span style={{ color: LINKD.lavender }} className="italic">dominant Gen-Z storyline</span>
                            <span style={{ color: LINKD.cream }}> of 2026.</span>
                        </Display>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                            {[
                                { icon: Flame, color: LINKD.orange, title: "Macro", body: "Indian Gen Z is leaving apps for 'meet-touch-feel' mixers. Bangalore Singles Network filling waitlists." },
                                { icon: TrendingUp, color: LINKD.amber, title: "Incumbents", body: "Bumble Hive · Date Generator · Puma mixers — bolting IRL onto swipe products." },
                                { icon: Target, color: LINKD.lavender, title: "The Gap", body: "No Indian @hinge.vinge equivalent. Hinglish + metro + voicey is wide open." },
                            ].map(({ icon: I, color, title, body }, i) => (
                                <div
                                    key={i}
                                    className="rounded-3xl p-7 backdrop-blur-sm border transition-transform hover:-translate-y-1"
                                    style={{ background: `${LINKD.ink}80`, borderColor: `${color}30` }}
                                >
                                    <I className="w-9 h-9 mb-4" style={{ color }} />
                                    <p className="font-bold text-2xl mb-2" style={{ color: LINKD.cream }}>{title}</p>
                                    <p className="text-sm leading-relaxed" style={{ color: `${LINKD.cream}80` }}>{body}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </Section>

                {/* ─── 4. THE WEDGE — full-bleed quote ─── */}
                <Section className="px-6">
                    <div className="max-w-5xl mx-auto text-center">
                        <Eyebrow n={2} color={LINKD.lavender}>Positioning</Eyebrow>
                        <div
                            className="relative rounded-[2rem] py-16 md:py-24 px-8 backdrop-blur-sm border overflow-hidden"
                            style={{ background: `${LINKD.ink}66`, borderColor: `${LINKD.lavender}30` }}
                        >
                            <div
                                className="absolute -top-20 -left-20 w-80 h-80 rounded-full filter blur-[80px] opacity-25"
                                style={{ background: LINKD.lavender }}
                            />
                            <div
                                className="absolute -bottom-20 -right-20 w-80 h-80 rounded-full filter blur-[80px] opacity-20"
                                style={{ background: LINKD.orange }}
                            />
                            <Display className="text-[clamp(1.75rem,4.5vw,3.75rem)] relative" italic>
                                <span style={{ color: LINKD.cream }}>For people who&apos;ve read every Hinge prompt twice</span>
                                <br />
                                <span style={{ color: LINKD.lavender }}>and still haven&apos;t been on a date this month.</span>
                            </Display>
                            <div className="grid grid-cols-3 gap-4 mt-14 max-w-2xl mx-auto relative">
                                {[
                                    { icon: Calendar, t: "Time > text", c: LINKD.amber },
                                    { icon: Flame, t: "Name the fatigue", c: LINKD.orange },
                                    { icon: Coffee, t: "Local, not global", c: LINKD.lavender },
                                ].map(({ icon: I, t, c }, i) => (
                                    <div key={i} className="flex flex-col items-center gap-2.5">
                                        <I className="w-6 h-6" style={{ color: c }} />
                                        <span className="text-xs md:text-sm font-medium" style={{ color: `${LINKD.cream}cc` }}>{t}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </Section>

                {/* ─── 5. MARKET MAP ─── */}
                <Section className="px-6">
                    <div className="max-w-6xl mx-auto">
                        <Eyebrow n={3}>Market Map</Eyebrow>
                        <Display className="text-[clamp(1.75rem,4vw,3rem)] mb-12">
                            <span style={{ color: LINKD.cream }}>Who owns what — </span>
                            <span style={{ color: LINKD.lavender }} className="italic">where they&apos;re weak.</span>
                        </Display>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {competitors.map((c, i) => (
                                <div
                                    key={i}
                                    className="rounded-2xl p-5 backdrop-blur-sm border transition-all hover:border-opacity-60"
                                    style={{ background: `${LINKD.ink}66`, borderColor: `${LINKD.cream}10` }}
                                >
                                    <div className="flex items-center gap-3 mb-3">
                                        <div
                                            className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold"
                                            style={{ background: `${LINKD.lavender}20`, color: LINKD.lavenderBright }}
                                        >
                                            {c.initial}
                                        </div>
                                        <p className="font-semibold text-sm" style={{ color: LINKD.cream }}>{c.name}</p>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs" style={{ color: `${LINKD.neonRose}cc` }}>
                                        <X className="w-3 h-3" />
                                        <span>{c.weak}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </Section>

                {/* ─── 6. 5 PILLARS ─── */}
                <Section className="px-6">
                    <div className="max-w-7xl mx-auto">
                        <Eyebrow n={4}>5 Content Pillars</Eyebrow>
                        <Display className="text-[clamp(1.75rem,4vw,3rem)] mb-12">
                            <span style={{ color: LINKD.cream }}>Hinglish. Dry. </span>
                            <span style={{ color: LINKD.lavender }} className="italic">Never motivational.</span>
                        </Display>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                            {pillars.map((p, i) => {
                                const I = p.icon;
                                return (
                                    <motion.div
                                        key={i}
                                        whileHover={{ y: -6 }}
                                        className="rounded-3xl p-6 backdrop-blur-sm border flex flex-col"
                                        style={{ background: `${LINKD.ink}80`, borderColor: `${p.color}30` }}
                                    >
                                        <div className="flex items-center justify-between mb-5">
                                            <div
                                                className="w-12 h-12 rounded-2xl flex items-center justify-center"
                                                style={{ background: `${p.color}20` }}
                                            >
                                                <I className="w-6 h-6" style={{ color: p.color }} />
                                            </div>
                                            <span className="text-2xl font-mono font-black opacity-30" style={{ color: LINKD.cream }}>{p.mix}%</span>
                                        </div>
                                        <h3 className="font-bold text-lg leading-tight" style={{ color: LINKD.cream }}>{p.name}</h3>
                                        <p className="text-xs mt-1 mb-5" style={{ color: `${LINKD.cream}55` }}>{p.sub}</p>
                                        <div className="border-t pt-4 mt-auto" style={{ borderColor: `${LINKD.cream}10` }}>
                                            <p className="text-xs italic leading-relaxed" style={{ color: `${LINKD.cream}88` }}>{p.hook}</p>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>
                </Section>

                {/* ─── 7. VOICE & VISUAL ─── */}
                <Section className="px-6">
                    <div className="max-w-6xl mx-auto">
                        <Eyebrow n={5}>Voice &amp; Visual</Eyebrow>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            {/* VOICE */}
                            <div
                                className="rounded-3xl p-7 backdrop-blur-sm border"
                                style={{ background: `${LINKD.ink}80`, borderColor: `${LINKD.cream}10` }}
                            >
                                <div className="flex items-center gap-2 mb-6">
                                    <MessageCircle className="w-5 h-5" style={{ color: LINKD.lavender }} />
                                    <h3 className="font-bold text-lg" style={{ color: LINKD.cream }}>Voice</h3>
                                    <span className="text-[10px] ml-auto font-mono tracking-widest uppercase" style={{ color: `${LINKD.cream}55` }}>
                                        Hinglish · dry · self-aware
                                    </span>
                                </div>
                                <div className="space-y-2.5">
                                    {[
                                        { ok: true, t: "47 chats. 0 dates. Still texting 'wyd'?" },
                                        { ok: true, t: "Bandra cafés ranked by escape difficulty." },
                                        { ok: false, t: "Love is the answer. Find your forever ✨" },
                                        { ok: false, t: "Be your authentic self." },
                                    ].map((v, i) => (
                                        <div
                                            key={i}
                                            className="flex items-start gap-3 p-3.5 rounded-2xl text-sm"
                                            style={{
                                                background: v.ok ? `${LINKD.mindaro}10` : `${LINKD.neonRose}10`,
                                                border: `1px solid ${v.ok ? LINKD.mindaro : LINKD.neonRose}25`,
                                            }}
                                        >
                                            {v.ok
                                                ? <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: LINKD.mindaroDeep }} />
                                                : <X className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: LINKD.neonRose }} />
                                            }
                                            <span style={{ color: v.ok ? LINKD.cream : `${LINKD.cream}55`, textDecoration: v.ok ? 'none' : 'line-through' }}>
                                                {v.t}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* VISUAL */}
                            <div
                                className="rounded-3xl p-7 backdrop-blur-sm border"
                                style={{ background: `${LINKD.ink}80`, borderColor: `${LINKD.cream}10` }}
                            >
                                <div className="flex items-center gap-2 mb-6">
                                    <Sparkles className="w-5 h-5" style={{ color: LINKD.lavender }} />
                                    <h3 className="font-bold text-lg" style={{ color: LINKD.cream }}>Visual</h3>
                                    <span className="text-[10px] ml-auto font-mono tracking-widest uppercase" style={{ color: `${LINKD.cream}55` }}>
                                        Diary, not dashboard
                                    </span>
                                </div>
                                <p className="text-[10px] uppercase tracking-[0.3em] mb-3" style={{ color: `${LINKD.cream}55` }}>Palette · Link&apos;d native</p>
                                <div className="grid grid-cols-6 gap-2 mb-5">
                                    {[
                                        { c: LINKD.night, n: "Night" },
                                        { c: LINKD.ink, n: "Ink" },
                                        { c: LINKD.cream, n: "Cream" },
                                        { c: LINKD.lavender, n: "Lavender" },
                                        { c: LINKD.orange, n: "Orange" },
                                        { c: LINKD.mindaro, n: "Mindaro" },
                                    ].map((s, i) => (
                                        <div key={i} className="text-center">
                                            <div
                                                className="aspect-square rounded-2xl mb-1.5 border"
                                                style={{ background: s.c, borderColor: `${LINKD.cream}15` }}
                                            ></div>
                                            <p className="text-[9px] font-mono tracking-wider" style={{ color: `${LINKD.cream}55` }}>{s.n}</p>
                                        </div>
                                    ))}
                                </div>
                                <p className="text-[10px] uppercase tracking-[0.3em] mb-3" style={{ color: `${LINKD.cream}55` }}>Type</p>
                                <div className="rounded-2xl p-4 mb-5 border" style={{ background: `${LINKD.cream}05`, borderColor: `${LINKD.cream}10` }}>
                                    <p
                                        className="text-3xl mb-1"
                                        style={{
                                            fontFamily: '"Fraunces", serif',
                                            fontVariationSettings: '"SOFT" 90, "WONK" 1',
                                            fontStyle: 'italic',
                                            color: LINKD.cream,
                                        }}
                                    >
                                        Fraunces
                                    </p>
                                    <p className="text-xs font-mono" style={{ color: `${LINKD.cream}66` }}>Inter · 300/400/500</p>
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div
                                        className="flex items-center gap-2 px-3 py-2 rounded-xl border"
                                        style={{ background: `${LINKD.amber}10`, borderColor: `${LINKD.amber}25` }}
                                    >
                                        <Coffee className="w-3.5 h-3.5" style={{ color: LINKD.amber }} />
                                        <span style={{ color: `${LINKD.cream}cc` }}>Polaroid · chai · café</span>
                                    </div>
                                    <div
                                        className="flex items-center gap-2 px-3 py-2 rounded-xl border"
                                        style={{ background: `${LINKD.neonRose}10`, borderColor: `${LINKD.neonRose}25` }}
                                    >
                                        <PhoneOff className="w-3.5 h-3.5" style={{ color: LINKD.neonRose }} />
                                        <span style={{ color: `${LINKD.cream}cc` }}>Phone-as-villain</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Section>

                {/* ─── 8. TIMELINE ─── */}
                <Section className="px-6">
                    <div className="max-w-6xl mx-auto">
                        <Eyebrow n={6}>90-Day Timeline</Eyebrow>
                        <Display className="text-[clamp(1.75rem,4vw,3rem)] mb-12">
                            <span style={{ color: LINKD.cream }}>Zero to 25K. First </span>
                            <span style={{ color: LINKD.lavender }} className="italic">1M-view reel.</span>
                        </Display>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 relative">
                            {phases.map((ph, i) => (
                                <div key={i} className="relative">
                                    <div
                                        className="rounded-3xl p-6 h-full backdrop-blur-sm border"
                                        style={{ background: `${LINKD.ink}80`, borderColor: `${ph.color}25` }}
                                    >
                                        <div className="h-1 rounded-full mb-5" style={{ background: ph.color }}></div>
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="font-mono text-xs tracking-widest" style={{ color: ph.color }}>{ph.tag}</span>
                                            <span className="font-mono text-xs" style={{ color: `${LINKD.cream}55` }}>{ph.weeks}</span>
                                        </div>
                                        <h3 className="font-bold text-lg mb-2" style={{ color: LINKD.cream }}>{ph.title}</h3>
                                        <p className="text-xs leading-relaxed" style={{ color: `${LINKD.cream}aa` }}>{ph.goal}</p>
                                    </div>
                                    {i < phases.length - 1 && (
                                        <ArrowRight
                                            className="hidden md:block absolute -right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 z-10"
                                            style={{ color: `${LINKD.lavender}66` }}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </Section>

                {/* ─── 9. ENGINE ─── */}
                <Section className="px-6">
                    <div className="max-w-6xl mx-auto">
                        <Eyebrow n={7}>Production Engine · monthly</Eyebrow>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                            {engine.map((e, i) => {
                                const I = e.icon;
                                return (
                                    <div
                                        key={i}
                                        className="rounded-3xl p-6 text-center backdrop-blur-sm border"
                                        style={{ background: `${LINKD.ink}80`, borderColor: `${LINKD.lavender}20` }}
                                    >
                                        <I className="w-7 h-7 mx-auto mb-3" style={{ color: LINKD.lavender }} />
                                        <p
                                            className="text-3xl font-black mb-1"
                                            style={{
                                                color: LINKD.lavenderBright,
                                                fontFamily: '"Fraunces", serif',
                                                fontVariationSettings: '"SOFT" 50',
                                            }}
                                        >
                                            {e.num}
                                        </p>
                                        <p className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: `${LINKD.cream}66` }}>
                                            {e.label}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>
                        <p className="text-center text-sm mt-8 italic" style={{ color: `${LINKD.cream}80` }}>
                            Same engine ships for <span style={{ color: LINKD.amber }}>Kush</span> and <span style={{ color: LINKD.amber }}>Sachin</span>. Day-one capacity.
                        </p>
                    </div>
                </Section>

                {/* ─── 10. KPIs ─── */}
                <Section className="px-6">
                    <div className="max-w-5xl mx-auto">
                        <Eyebrow n={8}>90-Day KPIs</Eyebrow>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {kpis.map((k, i) => (
                                <div
                                    key={i}
                                    className="rounded-3xl p-7 text-center backdrop-blur-sm border"
                                    style={{
                                        background: `${LINKD.ink}80`,
                                        borderColor: `${LINKD.lavender}30`,
                                        borderTopWidth: '3px',
                                        borderTopColor: LINKD.lavender,
                                    }}
                                >
                                    <p
                                        className="text-4xl md:text-5xl font-black mb-1"
                                        style={{
                                            color: LINKD.lavenderBright,
                                            fontFamily: '"Fraunces", serif',
                                            fontVariationSettings: '"SOFT" 50, "WONK" 0',
                                        }}
                                    >
                                        {k.num}
                                    </p>
                                    <p className="text-[10px] font-mono uppercase tracking-[0.35em]" style={{ color: `${LINKD.cream}66` }}>{k.label}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </Section>

                {/* ─── 11. OPEN QUESTIONS ─── */}
                <Section className="px-6">
                    <div className="max-w-6xl mx-auto">
                        <Eyebrow n={9}>Lock Before Kickoff</Eyebrow>
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                            {questions.map((q, i) => {
                                const I = q.icon;
                                return (
                                    <div
                                        key={i}
                                        className="rounded-2xl p-4 text-center backdrop-blur-sm border transition-all hover:-translate-y-1"
                                        style={{ background: `${LINKD.ink}66`, borderColor: `${LINKD.cream}10` }}
                                    >
                                        <I className="w-5 h-5 mx-auto mb-2" style={{ color: LINKD.lavender }} />
                                        <p className="text-[11px] font-medium leading-tight" style={{ color: `${LINKD.cream}cc` }}>{q.q}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </Section>

                {/* ─── 12. CTA ─── */}
                <Section className="px-6">
                    <div className="max-w-4xl mx-auto">
                        <div
                            className="relative rounded-[2rem] py-20 px-8 text-center backdrop-blur-sm border overflow-hidden"
                            style={{ background: `${LINKD.ink}80`, borderColor: `${LINKD.lavender}30` }}
                        >
                            <div
                                className="absolute -top-32 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full filter blur-[100px] opacity-30"
                                style={{ background: LINKD.lavender }}
                            />
                            <div className="relative">
                                <Heart className="w-10 h-10 mx-auto mb-6" style={{ color: LINKD.lavender }} />
                                <Display className="text-[clamp(2rem,5vw,3.75rem)] mb-4">
                                    <span style={{ color: LINKD.cream }}>One team. </span>
                                    <span style={{ color: LINKD.lavender }} className="italic">One number.</span>
                                </Display>
                                <p className="text-sm mb-10 max-w-md mx-auto" style={{ color: `${LINKD.cream}80` }}>
                                    Strategy → production → posting → community → reporting. No agency-of-record handoff seams.
                                </p>
                                <a
                                    href="https://calendly.com/suresh-socialseo/socialseo-instant-report-clone-1"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-3 px-9 py-4 rounded-full text-base font-bold transition-all duration-300 hover:scale-105"
                                    style={{
                                        background: `linear-gradient(135deg, ${LINKD.lavender}, ${LINKD.lavenderDeep})`,
                                        color: LINKD.night,
                                        boxShadow: `0 20px 50px -10px ${LINKD.lavender}55`,
                                    }}
                                >
                                    Book kickoff call <ArrowRight className="w-5 h-5" />
                                </a>
                                <p className="text-[10px] mt-5 font-mono tracking-[0.4em] uppercase" style={{ color: `${LINKD.cream}55` }}>
                                    15 min · 7 questions · go live in 14 days
                                </p>
                            </div>
                        </div>
                    </div>
                </Section>

                {/* ─── Footer ─── */}
                <footer className="max-w-5xl mx-auto text-center pt-8 pb-16 px-6 flex items-center justify-center gap-2.5">
                    <Zap className="w-3 h-3" style={{ color: LINKD.lavender }} />
                    <p className="text-[10px] font-mono tracking-[0.4em] uppercase" style={{ color: `${LINKD.cream}55` }}>
                        SocialSEO · Bangalore · 2026 · @linkd.social
                    </p>
                </footer>
            </div>
        </div>
    );
}
